// @file Broadcast channel for communication between tabs
import { can } from '@@/bits/browser'
import { setCookie } from '@@/bits/cookie'
import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import window, { browsingContextUid as tabUid } from '@@/bits/global'

function isInIframe(): boolean {
  return window.self !== window.top
}

export function shouldUseBroadcastChannel(): boolean {
  // Firefox doesn't allow to set up a BroadcastChannel within an iframe because of security reasons.
  return can('BroadcastChannel') && !isInIframe() && isAppUsing('broadcastChannel')
}

export const broadcastChannels: Record<string, BroadcastChannel> = {}

export function getTabId(): string {
  return window.name !== '' ? window.name : tabUid
}

function setActiveTabIdInCookie(): void {
  // Same as ACTIVE_TAB_ID_COOKIE in services/rails/app/models/powwow.rb.
  const ACTIVE_TAB_ID_COOKIE = 'ww_ati'
  const tabId = getTabId()
  setCookie(ACTIVE_TAB_ID_COOKIE, tabId)
}

export function initializeBroadcastChannel(channelName: string): BroadcastChannel | null {
  if (!shouldUseBroadcastChannel()) return null

  try {
    const broadcastChannel = new BroadcastChannel(channelName)
    broadcastChannels[channelName] = broadcastChannel

    const tabId = getTabId()
    window.name = tabId

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        setActiveTabIdInCookie()
      }
    })

    window.addEventListener('focus', () => {
      setActiveTabIdInCookie()
    })

    window.addEventListener('beforeunload', () => {
      broadcastChannel.close()
    })

    return broadcastChannel
  } catch (e) {
    captureException(e)
  }
  return null
}
